import { AccountPage } from "@common-components/account-page/account-page";
import BTThemeProvider from "@common-components/bt-theme-provider";
import { useRouter } from "@common-reducers/hooks/use-router";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import PlanSelection from "@common-components/plan-selection/plan-selection";
import PlanSelectionV2 from "@common-components/plan-selection-v2/plan-selection-v2";
import CheckoutErrorAlert from "@common-components/plan-selection/checkout-error-alert";
import { SideBarItem } from "@common-components/sidebar/sidebar";
import { User } from "@common/models/user";
import classes from "../account-view/account-view.module.scss";
import { openPaymentDialogThunk, openCheckoutPageThunk, startAnnualSaveCheckoutThunk } from "@common-reducers/PaymentFormThunks";
import { paymentFormActions } from "@common-reducers/PaymentFormReducer";
import { getWorkspaceDataThunk } from "@common-reducers/WorkspaceThunks";
import { selectCurrentWorkspace } from "@common-reducers/UserSelectors";
import { Stack } from "@mui/system";
import { Backdrop, CircularProgress } from "@mui/material";
import BTButton from "@common-components/bt-button/bt-button";
import { namespace } from "@common-config/constants";
import { useTranslation } from "react-i18next";
import { DBSubscriptionThunks } from "@common-reducers/DBServiceThunks";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import ReferralShareCard from "@common-components/referral-share-card/referral-share-card";

export interface AccountSubscriptionViewProps {
    /** The previous plans page, kept at /account-subscription-test for comparison. */
    legacy?: boolean;
}

export interface AccountSubscriptionViewState {
    user?: User | null | undefined;
    loading: boolean;
}



const AccountSubscriptionView = ({ legacy = false }: AccountSubscriptionViewProps) => {
    const { t } = useTranslation(namespace);
    const router = useRouter();
    const dispatch = useAppDispatch()
    const currentWorkspace = useAppSelector(selectCurrentWorkspace);
    const analyticsService = useAnalyticsService();

    useEffect(() => {
        if (router.query.purchaseSuccess !== undefined) {
            // Hosted-checkout return: the subscription is created server-side (Chargebee
            // webhook → chargebee.class.ts updateSubscription), so the FE never calls
            // finalizePurchase here. Refetch entitlement (DBSubscription + workspace) so the
            // new plan shows without a hard reload — mirrors the upgrade-success refetch.
            dispatch(DBSubscriptionThunks.find({}))
            if (currentWorkspace?._id) {
                dispatch(getWorkspaceDataThunk({ workspaceId: currentWorkspace._id }))
            }
            dispatch(openPaymentDialogThunk({ paymentSuccess: true }))
            router.navigate(router.pathname)
        }
    }, [dispatch, router, router.query.purchaseSuccess, currentWorkspace]);

    // Handle direct plan and discount/coupon parameters for automatic checkout redirect
    useEffect(() => {
        const { plan, discount, coupon } = router.query;
        const couponCode = discount || coupon;

        if (plan && typeof plan === 'string') {
            // Normalize plan name: lowercase first, then capitalize first letter
            const normalizedPlan = plan?.toLowerCase()?.charAt(0)?.toUpperCase() + plan?.toLowerCase()?.slice(1);

            // Let backend determine billing cycle based on current subscription
            const itemPriceId = `${normalizedPlan}-USD-Monthly`; // This will be processed by backend

            // Prepare checkout parameters
            const checkoutParams = {
                item_price_id: itemPriceId,
                quantity: 1,
                auto_detect_billing_cycle: true,
                ...(couponCode && typeof couponCode === 'string' && { coupon_ids: [couponCode] })
            };

            // Redirect to Chargebee checkout
            dispatch(openCheckoutPageThunk(checkoutParams));
        }
    }, [dispatch, router.query.plan, router.query.discount, router.query.coupon]);

    // #241 slice 3 — landing for the priced T-10 renewal email (?annualOffer=renewal_email).
    // The server re-checks eligibility and mints the 15% yearly-only coupon + hosted checkout
    // at click time; if the offer is gone (price changed, coupon added, flag off...) the
    // customer stays on the normal plans page with a plain notice, never a wrong price.
    const annualOfferStarted = useRef(false);
    const [annualOfferLoading, setAnnualOfferLoading] = useState(false);
    useEffect(() => {
        if (router.query.annualOffer !== 'renewal_email' || annualOfferStarted.current) return;
        annualOfferStarted.current = true;
        setAnnualOfferLoading(true);
        dispatch(startAnnualSaveCheckoutThunk({ source: 'renewal_email' }))
            .unwrap()
            .catch(() => {
                setAnnualOfferLoading(false);
                dispatch(paymentFormActions.setCheckoutErrorMessage(t('accountSubscriptionView.annualOfferUnavailable')));
                router.navigate(router.pathname);
            });
    }, [dispatch, router, router.query.annualOffer, t]);

    // Back from the hosted checkout can restore this page from bfcache with the spinner still up.
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) setAnnualOfferLoading(false);
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    useEffect(() => {
        document.title = t('accountSubscriptionView.buySubscription');
        
        // Track view_item event for subscription page visit
        analyticsService.ecommerceEvent('view_item', {
            currency: 'USD',
            items: [{
                item_id: 'subscription_plans_page',
                item_name: 'Subscription Plans',
                item_category: 'subscription',
                item_brand: 'Blueticks',
                quantity: 1
            }]
        }, {
            page_title: t('accountSubscriptionView.buySubscription'),
            page_location: window.location.href
        });
    }, [t, analyticsService]);


    useEffect(() => {
        dispatch(DBSubscriptionThunks.find({}));
    }, [])

    const noWorkspaceView = () => {
        return <Stack sx={{
            width: 1,
            height: 1
        }}
            spacing={2}
            direction="column"
            alignItems="center"
            justifyContent="center">
            <span>{t('accountSubscriptionView.mustSelectWorkspace')}</span>
            <BTButton onClick={() => router.navigate('/account-workspace')}>{t('accountSubscriptionView.selectWorkspace')}</BTButton>
        </Stack>
    }

    const subscriptionView = () => {
        // The redesigned page carries its own hero, so no page title above it.
        if (!legacy) {
            return <div className={`${classes.accountSettingBody} btcss`} style={{ paddingTop: 0 }}>
                {currentWorkspace ? <PlanSelectionV2 /> : noWorkspaceView()}
                <ReferralShareCard variant="page" />
            </div>;
        }
        return <div className={classes.pageContainer}>
            <div className={classes.titleContainer}>
                <h4 className={classes.pageTitle}>{t('accountSubscriptionView.buySubscription')}</h4>
            </div>
            <div className={`${classes.accountSettingBody} btcss`}>
                {currentWorkspace ? <PlanSelection /> : noWorkspaceView()}
                <ReferralShareCard variant="page" />
            </div>
        </div>;
    }

    return (<BTThemeProvider>
        <AccountPage page={subscriptionView()}></AccountPage>
        {/* Covers both checkout entry points on this page: the ?plan= deep link above and every
            ChargebeeButton rendered inside <PlanSelection />. */}
        <CheckoutErrorAlert />
        {/* Shown from the click on the renewal email until the redirect to the hosted checkout (or the notice). */}
        <Backdrop open={annualOfferLoading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
            <CircularProgress color="inherit" />
        </Backdrop>
    </BTThemeProvider>);
}

export default AccountSubscriptionView;